const u = up.util;
const e = up.element;
up.Preview = class Preview {
    constructor({ fragment, request, renderOptions, cleaner }) {
        this.fragment = fragment;
        this.request = request;
        this.renderOptions = renderOptions;
        this._cleaner = cleaner;
    }
    undo(...args) {
        if (this.ended) {
            reportError(new up.Error('Preview used after end of request'));
        }
        else {
            this._cleaner.guard(...args);
        }
    }
    get origin() {
        return this.request.origin;
    }
    get params() {
        return this.request.params;
    }
    get layer() {
        return this.request.layer;
    }
    get ended() {
        return this.request.ended;
    }
    get expiredResponse() {
        return this.renderOptions.expiredResponse;
    }
    get revalidating() {
        return !!this.expiredResponse;
    }
    run(value, options = {}) {
        for (let fn of up.status.resolvePreviewFns(value)) {
            this.undo(up.error.guard(fn, this, options));
        }
    }
    revert() {
        this._cleaner.clean();
    }
    setAttrs(...args) {
        let [element, attrs] = this._parseMutatorArgs(args, 'val', 'val');
        this.undo(e.setAttrsTemp(element, attrs));
    }
    addClass(...args) {
        let [element, klass] = this._parseMutatorArgs(args, 'val', 'val');
        this.undo(e.addClassTemp(element, klass));
    }
    addClassBatch(elements, classes) {
        for (let element of elements) {
            for (let klass of classes) {
                this.addClass(element, klass);
            }
        }
    }
    removeClass(...args) {
        let [element, klass] = this._parseMutatorArgs(args, 'val', 'val');
        this.undo(e.removeClassTemp(element, klass));
    }
    setStyle(...args) {
        let [element, styles] = this._parseMutatorArgs(args, 'val', 'val');
        this.undo(e.setStyleTemp(element, styles));
    }
    disable(...args) {
        let [element] = this._parseMutatorArgs(args, 'val');
        this.undo(up.form.disableTemp(element));
    }
    insert(...args) {
        let [reference, position = 'beforeend', tempValue] = this._parseMutatorArgs(args, 'val', u.isAdjacentPosition, 'val');
        this.undo(up.fragment.insertTemp(reference, position, tempValue));
    }
    show(...args) {
        let [element] = this._parseMutatorArgs(args, 'val');
        this.undo(e.showTemp(element));
    }
    hide(...args) {
        let [element] = this._parseMutatorArgs(args, 'val');
        this.undo(e.hideTemp(element));
    }
    hideContent(...args) {
        let [parent] = this._parseMutatorArgs(args, 'val');
        let wrapper = e.wrapChildren(parent);
        e.hide(wrapper);
        this.undo(() => e.unwrap(wrapper));
    }
    showPlaceholder(...args) {
        let [parent, placeholderReference] = this._parseMutatorArgs(args, 'val', 'val');
        let placeholderNodes = up.fragment.provideNodes(placeholderReference, { origin: this.origin });
        up.puts('[up-placeholder]', 'Showing placeholder %o', placeholderReference);
        if (parent) {
            this.swapContent(parent, placeholderNodes);
        }
        else if (this.layer === 'new') {
            this.openLayer(placeholderNodes, { closeAnimation: false });
            this.renderOptions.openAnimation = false;
        }
    }
    swapContent(...args) {
        let [parent, newContent] = this._parseMutatorArgs(args, 'val', 'val');
        this.hideContent(parent);
        this.insert(parent, newContent);
    }
    openLayer(content, options = {}) {
        let undoDismissValue = ':undo-preview';
        let onDismiss = ({ value }) => {
            if (value !== undoDismissValue)
                this.request.abort({ reason: 'Preview overlay dismissed' });
        };
        up.layer.open({
            ...u.pick(this.renderOptions, [...up.Layer.Overlay.VISUAL_KEYS, 'target']),
            ...options,
            content,
            abort: false,
            onDismiss
        });
        let overlay = up.layer.front;
        this.undo(() => overlay.dismiss(undoDismissValue, { preventable: false }));
        return overlay;
    }
    _parseMutatorArgs(args, ...specs) {
        let [element, ...rest] = u.args(args, ...specs);
        element = up.fragment.get(element, { layer: this.layer, origin: this.origin }) || this.fragment;
        return [element, ...rest];
    }
};
